import React from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { useHotel } from "../../contexts/HotelContext";
import { useAdminTenantDeletion } from "../../hooks/useAdminTenantDeletion";
import {
  DeleteStatusBadge,
  DeletionTypeBadge,
  SnapshotBadge,
} from "../../components/admin/tenant-deletion/StatusBadges";
import { TenantDeletionSpinner } from "../../components/admin/tenant-deletion/TenantDeletionSpinner";

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Read-only archive detail (AC-30, AC-32).
 * Shows tenant name, NIP, full reason, schema_version, allMigrations count, linked_party_ids count.
 * "Skontaktuj się z DevOps z linkiem do {archiveId}" section.
 * NO Restore button.
 */
const AdminTenantDeletionDetails: React.FC = () => {
  const { user } = useHotel();
  const { archiveId } = useParams<{ archiveId: string }>();
  const { data, isLoading, isError, error } = useAdminTenantDeletion(archiveId);

  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h3 className="text-amber-800 font-medium">Brak dostępu</h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <nav
          aria-label="Breadcrumb"
          data-testid="admin-tenant-deletion-detail-breadcrumb"
          className="text-sm text-neutral-500"
        >
          <Link
            to="/admin/tenant-deletions"
            className="hover:underline"
          >
            Archiwum usunięć
          </Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <span aria-current="page" className="text-neutral-700">
            {data?.tenantNameSnapshot ?? archiveId}
          </span>
        </nav>
        <div>
          <Link
            to="/admin/tenant-deletions"
            className="text-sm text-primary-600 hover:underline"
          >
            ← Wróć do listy
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-neutral-900">
            Szczegóły archiwum
          </h1>
          <p className="text-sm text-neutral-500 break-all">{archiveId}</p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center min-h-32">
            <TenantDeletionSpinner
              data-testid="admin-tenant-deletion-detail-loading"
              size="md"
              variant="primary"
            />
          </div>
        )}

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-red-800 font-medium">Błąd ładowania</h3>
            <p className="text-red-600 text-sm mt-1">
              {error instanceof Error ? error.message : "Nieznany błąd"}
            </p>
          </div>
        )}

        {data && (
          <div
            data-testid="admin-tenant-deletion-detail"
            className="space-y-6"
          >
            <section className="bg-white border border-neutral-200 rounded-md p-4">
              <h2 className="text-lg font-semibold text-neutral-900 mb-3">
                Tenant
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-neutral-500">Nazwa</dt>
                  <dd className="text-neutral-900">{data.tenantNameSnapshot}</dd>
                </div>
                <div>
                  <dt className="text-neutral-500">NIP</dt>
                  <dd className="text-neutral-900">
                    {data.tenantNipSnapshot || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Tenant ID</dt>
                  <dd className="text-neutral-900 break-all">{data.tenantId}</dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Typ usunięcia</dt>
                  <dd className="text-neutral-900">
                    <DeletionTypeBadge type={data.deletionType} />
                  </dd>
                </div>
              </dl>
            </section>

            <section className="bg-white border border-neutral-200 rounded-md p-4">
              <h2 className="text-lg font-semibold text-neutral-900 mb-3">
                Operacja
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-neutral-500">Wykonał</dt>
                  <dd className="text-neutral-900">
                    {data.deletedBy.name && data.deletedBy.name.trim().length > 0
                      ? `${data.deletedBy.name} (${data.deletedBy.email})`
                      : data.deletedBy.email}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Data</dt>
                  <dd className="text-neutral-900">
                    {formatDateTime(data.deletedAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Status</dt>
                  <dd className="text-neutral-900">
                    <DeleteStatusBadge status={data.deleteStatus} />
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Snapshot</dt>
                  <dd className="text-neutral-900">
                    <SnapshotBadge status={data.snapshotStatus} />
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-neutral-500">Powód</dt>
                  <dd className="text-neutral-900 whitespace-pre-wrap">
                    {data.reason || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Purge po</dt>
                  <dd className="text-neutral-900">
                    {formatDateTime(data.purgeAfter)}
                  </dd>
                </div>
                {data.failureReason && (
                  <div className="md:col-span-2">
                    <dt className="text-neutral-500">Powód błędu</dt>
                    <dd className="text-red-700 whitespace-pre-wrap">
                      {data.failureReason}
                    </dd>
                  </div>
                )}
                {typeof data.retryCount === "number" && (
                  <div>
                    <dt className="text-neutral-500">Liczba prób ponowienia</dt>
                    <dd className="text-neutral-900">{data.retryCount}</dd>
                  </div>
                )}
                {data.phase2StartedAt && (
                  <div>
                    <dt className="text-neutral-500">Faza 2 — rozpoczęta</dt>
                    <dd className="text-neutral-900">
                      {formatDateTime(data.phase2StartedAt)}
                    </dd>
                  </div>
                )}
                {data.phase2CompletedAt && (
                  <div>
                    <dt className="text-neutral-500">Faza 2 — zakończona</dt>
                    <dd className="text-neutral-900">
                      {formatDateTime(data.phase2CompletedAt)}
                    </dd>
                  </div>
                )}
              </dl>
            </section>

            {(data.schemaVersion ||
              typeof data.allMigrationsCount === "number" ||
              typeof data.linkedPartyIdsCount === "number") && (
              <section className="bg-white border border-neutral-200 rounded-md p-4">
                <h2 className="text-lg font-semibold text-neutral-900 mb-3">
                  Schema / Powiązania
                </h2>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {data.schemaVersion?.lastMigrationName && (
                    <div>
                      <dt className="text-neutral-500">Ostatnia migracja</dt>
                      <dd className="text-neutral-900 break-all">
                        {data.schemaVersion.lastMigrationName}
                      </dd>
                    </div>
                  )}
                  {data.schemaVersion?.snapshotSchemaVersion && (
                    <div>
                      <dt className="text-neutral-500">Schema version</dt>
                      <dd className="text-neutral-900">
                        {data.schemaVersion.snapshotSchemaVersion}
                      </dd>
                    </div>
                  )}
                  {typeof data.allMigrationsCount === "number" && (
                    <div>
                      <dt className="text-neutral-500">Liczba migracji</dt>
                      <dd className="text-neutral-900">
                        {data.allMigrationsCount}
                      </dd>
                    </div>
                  )}
                  {typeof data.linkedPartyIdsCount === "number" && (
                    <div>
                      <dt className="text-neutral-500">
                        Powiązane rekordy party (billing)
                      </dt>
                      <dd className="text-neutral-900">
                        {data.linkedPartyIdsCount}
                      </dd>
                    </div>
                  )}
                </dl>
              </section>
            )}

            {/* DevOps contact section — NO restore button */}
            <section
              data-testid="admin-tenant-deletion-devops-contact"
              className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900"
            >
              <p className="font-medium mb-1">Restore wymaga DevOps.</p>
              <p>
                Skontaktuj się z DevOps z linkiem do <code>{data.id}</code>.
              </p>
              <p className="mt-2 text-amber-700">
                Snapshot purgowany po 30 dniach. Po purge restore nie jest możliwy.
              </p>
            </section>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTenantDeletionDetails;
